/* Live analysis: note, frequency and cents, vibrato rate/extent and the strummed guitar chord. */
import { useRef } from 'react';
import { isSingleNote } from '../../core/dsp';
import { matchChordFrame, useMicChord, useMicFrame } from '../../core/mic';
import { chordSymbol, noteName, pretty } from '../../core/theory';
import { fmtDec } from '../../core/util';
import { PitchGraph, type PitchGraphHandle } from '../../ui/PitchGraph';
import { MAJMIN7 } from '../akor/vocab';
import { MicGate } from '../exercise/MicBlock';
import { ToolShell } from './ToolShell';

export function Analyzer() {
  const vNote = useRef<HTMLDivElement>(null);
  const vHz = useRef<HTMLDivElement>(null);
  const vCents = useRef<HTMLDivElement>(null);
  const vVib = useRef<HTMLDivElement>(null);
  const vVibDepth = useRef<HTMLDivElement>(null);
  const vChord = useRef<HTMLDivElement>(null);
  const graph = useRef<PitchGraphHandle>(null);
  const s = useRef({ hist: [] as [number, number][], center: 62, lastChordT: 0 }).current;
  const set = (r: React.RefObject<HTMLDivElement | null>, text: string) => {
    if (r.current) r.current.textContent = text;
  };

  useMicFrame((f) => {
    graph.current?.setNow(f.t);
    if (!f.voiced || f.midi == null) {
      graph.current?.push(f.t, null);
      const last = s.hist[s.hist.length - 1];
      if (last && f.t - last[0] > 0.3) s.hist.length = 0;
      return;
    }
    const m = Math.round(f.midi);
    const c = Math.round((f.midi - m) * 100);
    set(vNote, noteName(m));
    set(vHz, `${fmtDec(f.freq, 1)} Hz`);
    set(vCents, `${c >= 0 ? '+' : ''}${c} sent`);
    graph.current?.push(f.t, f.midi, Math.abs(c) <= 15 ? 'good' : Math.abs(c) <= 30 ? 'warn' : 'bad');
    s.center += (f.midi - s.center) * 0.03;
    graph.current?.setRange(Math.round(s.center) - 9, Math.round(s.center) + 9);
    s.hist.push([f.t, f.midi]);
    while (s.hist.length && f.t - (s.hist[0] as [number, number])[0] > 1.6) s.hist.shift();
    const first = s.hist[0];
    const last = s.hist[s.hist.length - 1];
    if (first && last && s.hist.length > 40 && last[0] - first[0] > 1.0) {
      // detrend with a moving average, then count zero crossings
      const xs = s.hist.map((p) => p[1]);
      const k = 7;
      const det = xs.map((x, i) => {
        let sum = 0;
        let n = 0;
        for (let j = Math.max(0, i - k); j <= Math.min(xs.length - 1, i + k); j++) {
          sum += xs[j] as number;
          n++;
        }
        return x - sum / n;
      });
      let zc = 0;
      for (let i = 1; i < det.length; i++) if ((det[i - 1] as number) < 0 !== (det[i] as number) < 0) zc++;
      const dur = last[0] - first[0];
      const rate = zc / 2 / dur;
      const mid = det.slice(k, -k);
      const ext = (Math.max(...mid) - Math.min(...mid)) * 50;
      const vib = rate >= 3.5 && rate <= 9 && ext >= 12;
      set(vVib, vib ? `${fmtDec(rate, 1)} Hz` : 'düz ses');
      set(vVibDepth, vib ? `±${Math.round(ext)} sent` : '');
    }
  });

  useMicChord((c) => {
    // silence, or a single sung or played note (its harmonics alone would read as a major chord)
    if (!c.active || isSingleNote(c.chroma, MAJMIN7)) {
      if (c.t - s.lastChordT > 1.2) set(vChord, '—');
      return;
    }
    const top = matchChordFrame(c, MAJMIN7)[0];
    if (top && top.score > 0.62) {
      set(vChord, pretty(chordSymbol(top.root, top.q)));
      s.lastChordT = c.t;
    }
  });

  return (
    <ToolShell
      title="Canlı ses analizi"
      desc="Söylediğin ya da çaldığın notayı, sapmasını, vibratonu ve gitar akorunu canlı izle."
      icon="wave"
    >
      <MicGate text="Analiz için mikrofon gerekir." />
      <div className="analyzer">
        <div className="an-top">
          <div className="an-cell">
            <span className="an-k">Nota</span>
            <div className="an-v" ref={vNote} data-testid="an-note">
              —
            </div>
          </div>
          <div className="an-cell">
            <span className="an-k">Frekans · sapma</span>
            <div className="an-v" ref={vHz} data-testid="an-hz">
              —
            </div>
            <div className="an-d" ref={vCents} data-testid="an-cents" />
          </div>
          <div className="an-cell">
            <span className="an-k">Vibrato</span>
            <div className="an-v" ref={vVib} data-testid="an-vib">
              —
            </div>
            <div className="an-d" ref={vVibDepth} data-testid="an-vib-depth" />
          </div>
          <div className="an-cell">
            <span className="an-k">Akor (gitar)</span>
            <div className="an-v hand" ref={vChord} data-testid="an-chord">
              —
            </div>
          </div>
        </div>
        <PitchGraph ref={graph} lo={52} hi={72} height={260} seconds={6} nowFrac={0.96} />
        <p className="mb-note">
          Vibrato için bir notayı en az bir saniye tut. Akor tanıma için gitarda bir akoru birkaç kez çal.
        </p>
      </div>
    </ToolShell>
  );
}
